import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `versions` — P11-02.
 *
 * PUBLISH AND RESTORE ARE THE SAME ACT, AND THIS SCREEN DOES NOT PRETEND OTHERWISE. P7-04
 * built them as **one handler** over
 * `UPDATE voice_agent SET live_version_id = $target, live_version_seq = live_version_seq + 1
 *  WHERE live_version_seq = $expected`, returning 409 on a stale sequence. This tab calls
 * that, quotes back the sequence it last read, and on a conflict **refetches and says so**
 * rather than retrying with the same number — retrying would be asking to overwrite a
 * decision it has not seen. The toast the design asks for is the confirmation of that one
 * row update, and it says the true thing about conversations in flight: they finish on the
 * version they were admitted on, because they hold the configuration they resolved then.
 *
 * AC-15 IS THE OTHER HALF OF THIS SCREEN. *A failing test case does not block publishing and
 * is recorded against the published version.* Two consequences are visible here and both are
 * deliberate:
 *
 *   1. **A failing case is drawn as a failure** — a critical tag, a critical glyph, the
 *      result text and the transcript excerpt that shows it. It is never a missing tick or a
 *      "4 of 5" with the fifth left out, because a quiet absence is exactly the thing AC-15
 *      exists to prevent.
 *   2. **Nothing here is disabled because of it.** Publish stays available with the failure
 *      on screen beside it, and the run records which `agent_version_id` it was against, so
 *      the failure remains findable against the version that was published anyway.
 *
 * WHAT THE DESIGN ASKS FOR THAT IS NOT BUILT, AND WHY. Each is drawn where the design puts it,
 * disabled and marked "soon" (the product owner's copy rule, 2026-09-23), rather than live
 * and posting nowhere:
 *
 *   · **"Run against the draft" and "Add a case".** No operation runs a test set or adds a case
 *     yet; the Test tab and the Test console place single test conversations.
 *   · **"Compare versions".** There is no comparison operation, and a diff assembled in the
 *     browser from two `config_document`s would be this console's opinion of what changed
 *     rather than the factory's record of it — which is what `change_summary` already is.
 *   · **"Download documents".** `listAgentVersionDocuments` returns a blob *name*, not a URL.
 *     Design §8.8 makes handing out stored content an audited act with its own route, and
 *     that route does not exist yet. A link that cannot resolve is worse than its absence.
 *
 * AMENDED 2026-09-25 (customer-portal review #75, #36, #37, #95; fix round P):
 *
 *   · **"Edit agent" / "Edit from here" are built** (`./edit-panel.tsx`). The panel opens here with
 *     the version's answers; saving builds a NEW version while the live one keeps answering, and the
 *     tab then selects it. While any version builds the list is re-read every few seconds; a built,
 *     never-published version offers "Test it in a chat" (a text test on THAT version, not the live
 *     one) beside "Publish". A change to "Record calls" alone takes effect at once, without a build.
 *   · The test-set copy no longer names an internal ticket (#36).
 *   · The paid review is left out while there is no live version, and draws nothing until there are
 *     calls to review (#37, `./optimisation-review-panel.tsx`).
 *   · "Published … by someone on your team" names the person, or says the first build published it
 *     (#95): `published_by_*` and `authored_by_name` on the version list.
 *
 * AMENDED 2026-10-09 (design sync): the history (list and detail) comes first; "Improve your agent"
 * follows under its own heading, with the paid review and then the test set. A built version that was
 * never published reads **Draft** and offers "Publish vN"; an older one "Restore vN"; the live one
 * "Edit from here". The detail's second figure is the template's own measure with its change against
 * the previous published version ("+3 pts vs v7"). "Download menu" / "Download questions" stay drawn
 * and disabled for the reason below. The test set's "Run against the draft" is drawn only while there is
 * a draft to run against.
 *
 * AMENDED 2026-10-09 (plan-015 DRF, gap G9, ADR 0043 — owner answer 3): answers saved on the Review tab and a
 * paid review's accepted fixes are DRAFTS now, published here by whoever may publish today. Each draft records
 * the version that was live when it was made (`listAgentVersionLineage`); when that is no longer the live
 * version the draft's detail says so before the click — "Built on v4; v6 is live now. Publishing replaces v6's
 * changes." — and "Publish vN" then sends the acknowledgement the API asks for (`publishDraftAgentVersion`). A
 * `409 draft_base_changed` the screen did not foresee (another version went live meanwhile) is shown in the
 * API's words with "Publish vN anyway". Answer and review drafts also offer "Recreate on the current version"
 * (the same answers or fixes on the live version, as a new draft); an edit's draft offers "Edit again".
 *
 * AMENDED 2026-10-13 (plan-018 WAPP-B, design §30.12, ADR 0059): under the history, a version with an information index
 * (the "Customer support" agent_template's) shows what it answers from — its pasted text, documents, website address and
 * the website reading it was built from, page by page — and, on the live version, "Read the website again", which builds
 * a draft from a fresh reading (`./information-panel.tsx`). Every other version draws nothing there.
 *
 * AMENDED 2026-10-28 (plan-030 WAPP-Q, design §41.10, §41.9): a survey version's "Download questions" is built — its
 * questionnaire as JSON (uploaded back through Edit it is used as it is) or as a readable page, per language, audited by
 * the api — and "Questions" opens it on the Questions tab. A draft made by the survey question editor says so, and its
 * "Edit again" opens the Questions tab. Before a survey draft is published, the campaigns calling with this agent are
 * named (OD-8): people called after the publish hear the new questions. The Edit panel asks before a new questionnaire
 * file replaces questions edited on the Questions tab.
 *
 * THE PER-VERSION FIGURES ("1,284 calls served", "81% orders placed") come from the Analytics
 * read over all time, which already carries one row per version. The version summary itself
 * has no counts, and the console does not compute them a second way.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Icon, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useMemberCan } from "../../../shell/member-capability.js";
import { TestChatPanel } from "../../test-console/test-chat-panel.js";
import { useGateway } from "../../../api/provider.js";
import {
  QUERY_KEY,
  agentVersionListQuery,
  testSuiteQuery,
  voiceAgentAnalyticsQuery,
  voiceAgentQuery,
} from "../../../api/query.js";
import type {
  AgentVersionComparisonView,
  AgentVersionSummaryView,
  ConsoleGateway,
  TestCaseResultView,
  TestSuiteView,
} from "../../../api/gateway.js";
import { draftBaseChangedOf, type AgentVersionLineageView } from "../../../api/slice/optimisation-review.js";
import { OptimisationReviewPanel } from "./optimisation-review-panel.js";
import { isSurveyAgentTemplate } from "./agent-tabs.js";
import { EditPanel, type EditOutcome } from "./edit-panel.js";
import { InformationPanel, RereadNotice } from "./information-panel.js";
import type { WebsiteRereadView } from "../../../api/slice/website-reading.js";
import { VOICE_AGENT_PASS_QUERY_KEY } from "../../../api/slice/voice-agent-pass.js";
import { SURVEY_QUESTIONNAIRE_QUERY_KEY } from "../../../api/slice/survey-questionnaire.js";
import { CampaignDiallingWarning, DownloadActions } from "./questions/questions-tab.js";
import { editorText } from "./questions/questionnaire-copy.js";

/** How often the list is re-read while a version builds: the new version turns ready on its own. */
const BUILDING_POLL_MS = 4_000;

/** What the last publish or restore did, so the toast can say it in the customer's words. */
interface PublishOutcome {
  readonly kind: "published" | "restored" | "conflict" | "base_changed" | "recreated";
  readonly label: string;
  /** `base_changed`: the API's sentence; `recreated`: unused. */
  readonly message?: string;
  /** `base_changed`: the draft to publish anyway. */
  readonly agentVersionId?: string;
}

/**
 * Each version's lineage (plan-015 DRF), under the version list's own key so every invalidation of the list
 * refreshes it too. The sandbox records no base, so its versions read as having none.
 */
export const agentVersionLineageQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentVersion, voiceAgentId, "lineage"],
    queryFn: (): Promise<readonly AgentVersionLineageView[]> => gateway.listAgentVersionLineage(voiceAgentId),
  });

/**
 * "Built on v4; v6 is live now. Publishing replaces v6's changes." — for a draft whose base is no longer the live
 * version, or `null` (not a draft, no recorded base, base still live).
 */
export function baseWarningOf(
  one: AgentVersionSummaryView,
  lineage: AgentVersionLineageView | undefined,
  liveVersion: AgentVersionSummaryView | undefined,
): string | null {
  if (!isDraft(one) || lineage === undefined || lineage.baseAgentVersionId === null || lineage.baseIsLive) return null;
  const base = lineage.baseLabel ?? portalText("operations", "an_earlier_version_7ee12768");
  if (liveVersion === undefined) return portalText("operations", "built_on_value_which_is_no_longer_live_b4131f5b", { v0: String(base) });
  return portalText("operations", "built_on_value_value_is_live_now_publishing_replaces_value_2f37c71b", { v0: String(base), v1: String(liveVersion.label), v2: String(liveVersion.label) });
}

export interface AgentVersionsTabProps {
  readonly voiceAgentId: string;
  /** The selected version, mirrored into the URL so a version is a link. */
  readonly selectedAgentVersionId: string;
  readonly onSelect: (agentVersionId: string) => void;
  /**
   * The Channels tab, where "Pass to another agent" is edited: "Edit again" on a pass-target draft whose base is no
   * longer live opens it there, not in the version editor (plan-021 review L6).
   */
  readonly onOpenChannelBindingTab: () => void;
  /**
   * plan-030 WAPP-Q: the Questions tab, on a version (`null`: the version the editor edits) — for a survey agent only;
   * `null` for every other agent.
   */
  readonly onOpenQuestions: ((agentVersionId: string | null) => void) | null;
}

export function AgentVersionsTab(props: AgentVersionsTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const voiceAgent = useQuery(voiceAgentQuery(gateway, props.voiceAgentId));
  const agentVersion = useQuery({
    ...agentVersionListQuery(gateway, props.voiceAgentId),
    refetchInterval: (query) => (query.state.data?.some((one) => one.state === "building") === true ? BUILDING_POLL_MS : false),
  });
  const testSuite = useQuery(testSuiteQuery(gateway, props.voiceAgentId));
  const analytics = useQuery(voiceAgentAnalyticsQuery(gateway, props.voiceAgentId, "all"));
  const lineage = useQuery(agentVersionLineageQuery(gateway, props.voiceAgentId));
  // plan-030 WAPP-Q (OD-8): the campaigns calling with a survey agent, named before one of its drafts is published.
  const surveyVoiceAgent = voiceAgent.data !== undefined && isSurveyAgentTemplate(voiceAgent.data.agentTemplateCode);
  const questionnaire = useQuery({
    queryKey: SURVEY_QUESTIONNAIRE_QUERY_KEY.document(props.voiceAgentId, null),
    queryFn: () => gateway.readQuestionnaireDocument(props.voiceAgentId, null),
    enabled: surveyVoiceAgent,
    retry: false,
  });
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null);
  // Review #75: the Edit panel (from the live version when `agentVersionId` is null), what the last
  // save did, and which built version is being tried in a chat.
  const [editing, setEditing] = useState<{ readonly agentVersionId: string | null } | null>(null);
  const [edited, setEdited] = useState<EditOutcome | null>(null);
  const [testingVersionId, setTestingVersionId] = useState<string | null>(null);
  // plan-018 WAPP-B: the draft "Read the website again" last opened.
  const [reread, setReread] = useState<WebsiteRereadView | null>(null);
  const editRefusal = useMemberCan().whyNot("voice_agent.write");

  const publish = useMutation({
    /**
     * ONE ACT, TWO WORDS, AND THE WORD IS NOT COSMETIC.
     *
     * P7-04 serves publish and restore from the same handler over the same row update, and
     * the only thing that differs is the `audit_entry.action_code` it writes. So the console
     * picks by the fact that decides it: a version that has never been published is being
     * **published**, one that has is being **restored** — which is what the customer will
     * read back out of the audit log later.
     */
    async mutationFn(intent: {
      readonly agentVersionId: string;
      readonly label: string;
      readonly everPublished: boolean;
      /** plan-015 DRF: the person saw that the draft's base is no longer live. */
      readonly acknowledgeBaseChange: boolean;
    }) {
      const current = voiceAgent.data;
      if (current === undefined) {
        throw new Error(portalText("operations", "the_agent_has_not_loaded_so_there_is_no_sequence_to_quote_5a9bf163"));
      }
      if (intent.everPublished) {
        await gateway.restoreAgentVersion(props.voiceAgentId, intent.agentVersionId, current.liveVersionSeq);
      } else {
        await gateway.publishDraftAgentVersion(
          props.voiceAgentId,
          intent.agentVersionId,
          current.liveVersionSeq,
          intent.acknowledgeBaseChange,
        );
      }
      return intent;
    },
    async onSuccess(intent) {
      setOutcome({
        kind: intent.everPublished ? "restored" : "published",
        label: intent.label,
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      // plan-021 review L1: the pass panel reads the published list, its draft and the sequence — all just changed.
      await queryClient.invalidateQueries({ queryKey: VOICE_AGENT_PASS_QUERY_KEY.all });
    },
    async onError(error, intent) {
      // plan-015 DRF: the draft is built on a version that is no longer live. Say so in the API's words and let
      // the person publish it anyway — nothing was changed.
      const baseChanged = draftBaseChangedOf(error);
      setOutcome(
        baseChanged === null
          ? { kind: "conflict", label: "" }
          : { kind: "base_changed", label: intent.label, message: baseChanged.message, agentVersionId: intent.agentVersionId },
      );
      // A 409 means somebody else published while this tab was open. Refetch and say so;
      // do NOT retry with the sequence that was just refused.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await queryClient.invalidateQueries({ queryKey: VOICE_AGENT_PASS_QUERY_KEY.all });
    },
  });

  /**
   * "Recreate on the current version" (plan-015 DRF): the same answers, or the same review's fixes, made again
   * as a new draft on the live version. The new draft is then selected.
   */
  const recreate = useMutation({
    async mutationFn(source: { readonly kind: "answers" } | { readonly kind: "optimisation_review"; readonly optimisationReviewId: string }) {
      const current = voiceAgent.data;
      if (current === undefined) throw new Error(portalText("operations", "the_agent_has_not_loaded_so_there_is_no_sequence_to_quote_5a9bf163"));
      if (source.kind === "optimisation_review") {
        return (await gateway.publishOptimisationReview(source.optimisationReviewId, current.liveVersionSeq)).agentVersionId;
      }
      await gateway.publishAnswers(props.voiceAgentId, current.liveVersionSeq);
      return null;
    },
    async onSuccess(agentVersionId) {
      setOutcome({ kind: "recreated", label: "" });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.gap });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.optimisationReview });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      const listed = await queryClient.fetchQuery(agentVersionListQuery(gateway, props.voiceAgentId));
      // The answers' response names no version; the newest draft is the one just made.
      const made = agentVersionId ?? newestDraftOf(listed)?.agentVersionId;
      if (made !== undefined) props.onSelect(made);
    },
    async onError() {
      setOutcome({ kind: "conflict", label: "" });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
    },
  });

  // AMENDED 2026-10-11: a list that cannot be read says so, instead of "Reading…" for ever.
  const failed = voiceAgent.error ?? agentVersion.error;
  if (failed !== null && (voiceAgent.data === undefined || agentVersion.data === undefined)) {
    return (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "the_history_could_not_be_read_84878e50")}
        {failed instanceof Error ? failed.message : String(failed)}
      </p>
    );
  }
  if (voiceAgent.data === undefined || agentVersion.data === undefined) {
    return <p className="c-note">{portalText("operations", "reading_the_version_history_a6aaa674")}</p>;
  }

  const all = agentVersion.data;
  const liveVersion = all.find((one) => one.isLive);
  const selected = all.find((one) => one.agentVersionId === props.selectedAgentVersionId) ?? liveVersion ?? all[0];
  const figuresOf = (agentVersionId: string): AgentVersionComparisonView | undefined =>
    analytics.data?.agentVersion.find((one) => one.agentVersionId === agentVersionId);
  const selectedFigures = selected === undefined ? undefined : figuresOf(selected.agentVersionId);
  const delta = selected === undefined ? null : successDeltaOf(selected, all, figuresOf);
  const survey = isSurveyAgentTemplate(voiceAgent.data.agentTemplateCode);
  const selectedLineage = selected === undefined ? undefined : lineage.data?.find((one) => one.agentVersionId === selected.agentVersionId);
  const baseWarning = selected === undefined ? null : baseWarningOf(selected, selectedLineage, liveVersion);

  return (
    <>
      <div className="c-va-titlerow c-va-titlerow-loose">
        <div>
          <p className="c-va-summary">
            {liveVersion === undefined
              ? portalText("operations", "no_version_is_answering_yet_every_edit_creates_a_new_versi_c7fbdbf6")
              : portalText("operations", "value_is_answering_calls_every_edit_creates_a_new_version_78daa05a", { v0: String(liveVersion.label) })}
          </p>
          <p className="c-va-subtitle">{portalText("operations", "version_history_91f86581")}</p>
        </div>
        <div className="c-va-actions">
          <Button variant="secondary" disabled>
            {portalText("operations", "compare_versions_soon_6edaafbd")}</Button>
          <span title={editRefusal ?? (liveVersion === undefined ? portalText("operations", "edit_once_a_version_has_built_53324e79") : undefined)}>
            <Button
              variant="primary"
              disabled={liveVersion === undefined || editRefusal !== null || editing !== null}
              onClick={() => {
                setEdited(null);
                setEditing({ agentVersionId: null });
              }}
            >
              <Icon glyph={GLYPH.pencilSimple} size="md" />
              <span>{portalText("operations", "edit_5301648d")}</span>
            </Button>
          </span>
        </div>
      </div>

      {outcome === null ? null : outcome.kind === "conflict" ? (
        <p className="c-va-alert c-va-alert-critical" role="status">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.warningCircle} size="lg" />
          </span>
          <span>
            {portalText("operations", "somebody_else_published_while_you_were_deciding_so_nothing_aeacd349")}</span>
        </p>
      ) : outcome.kind === "base_changed" ? (
        <div className="c-va-alert c-va-alert-critical" role="status">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.warningCircle} size="lg" />
          </span>
          <span>
            {outcome.message} {portalText("operations", "nothing_was_changed_c60d5d2a")}{" "}
            <Button
              variant="ghost"
              disabled={publish.isPending}
              onClick={() => {
                if (outcome.agentVersionId !== undefined) {
                  publish.mutate({
                    agentVersionId: outcome.agentVersionId,
                    label: outcome.label,
                    everPublished: false,
                    acknowledgeBaseChange: true,
                  });
                }
              }}
            >
              {portalText("operations", "publish_7db78801")}{outcome.label} {portalText("operations", "anyway_736b67b3")}</Button>
          </span>
        </div>
      ) : outcome.kind === "recreated" ? (
        <p className="c-va-alert" role="status">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.checkCircle} size="lg" />
          </span>
          <span>
            {portalText("operations", "a_new_draft_is_made_on_cd692b46")}{liveVersion?.label ?? portalText("operations", "the_live_version_ca0fc912")} {portalText("operations", "with_the_same_changes_it_answers_no_calls_until_you_publis_c012109c")}</span>
        </p>
      ) : (
        // Design sync 2026-10-09 (the Test rail export's `ver.restored`): a toast, bottom right.
        <div className="c-va-toast" role="status">
          <span className="c-va-toast-mark">
            <Icon glyph={GLYPH.arrowCounterClockwise} size="lg" />
          </span>
          <span className="c-va-toast-copy">
            <span className="c-va-toast-title">
              {outcome.kind === "restored" ? portalText("operations", "value_is_live_again_8cfda4f6", { v0: String(outcome.label) }) : portalText("operations", "value_is_live_91cf8d55", { v0: String(outcome.label) })}
            </span>
            <span className="c-va-toast-dek">
              {portalText("operations", "the_previous_version_stays_in_history_so_you_can_go_back_a_80a7f8fd")}</span>
            <span className="c-va-toast-actions">
              <Button
                variant="ghost"
                onClick={() => {
                  setOutcome(null);
                }}
              >
                {portalText("operations", "dismiss_70afe9ef")}</Button>
            </span>
          </span>
        </div>
      )}

      {edited === null ? null : <EditedNotice outcome={edited} liveLabel={liveVersion?.label ?? null} />}
      {reread === null ? null : <RereadNotice made={reread} liveLabel={liveVersion?.label ?? null} />}

      {editing === null ? null : (
        <EditPanel
          voiceAgentId={props.voiceAgentId}
          agentVersionId={editing.agentVersionId}
          questionsEdited={survey && (lineage.data ?? []).some((one) => one.draftSource === "questionnaire")}
          onClose={() => {
            setEditing(null);
          }}
          onDone={(done) => {
            setEditing(null);
            setEdited(done);
            if (done.kind === "building") props.onSelect(done.agentVersionId);
          }}
        />
      )}

      <div className="c-va-versions">
        <div className="c-va-version-list">
          {all.map((one) => {
            const figures = figuresOf(one.agentVersionId);
            return (
              <button
                key={one.agentVersionId}
                type="button"
                className="c-va-version-row"
                aria-current={one.agentVersionId === selected?.agentVersionId}
                onClick={() => {
                  props.onSelect(one.agentVersionId);
                }}
              >
                <span className="c-va-version-label">{one.label}</span>
                <span className="c-va-version-body">
                  <span className="c-va-version-head">
                    <span className="c-va-version-date">{one.publishedAtText ?? one.createdAtText}</span>
                    <AgentVersionStateTag one={one} liveAgentVersion={liveVersion} />
                  </span>
                  <span className="c-va-version-note">{one.note ?? portalText("operations", "no_note_8cb0e796")}</span>
                  <span className="c-va-version-by">
                    {authorLine(one)}
                    {figures === undefined
                      ? null
                      : portalText("operations", "value_calls_served_2fff6ecd", { v0: String(figures.conversationCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))) })}
                  </span>
                </span>
                <span className="c-va-version-caret">
                  <Icon glyph={GLYPH.caretRight} size="md" />
                </span>
              </button>
            );
          })}
        </div>

        {selected === undefined ? null : (
          <div className="c-va-card c-va-version-detail">
            <div className="c-va-version-detail-head">
              <p className="c-va-subtitle">{selected.label}</p>
              <AgentVersionStateTag one={selected} liveAgentVersion={liveVersion} />
            </div>
            <p className="c-va-version-published">
              {selected.publishedAtText === null
                ? portalText("operations", "created_value_by_value_never_published_cdf44312", { v0: String(selected.createdAtText), v1: String(authorOf(selected)) })
                : portalText("operations", "published_value_value_d9cb4f0c", { v0: String(selected.publishedAtText), v1: String(publisherOf(selected)) })}
            </p>
            <p className="c-va-version-detail-note">{selected.note ?? portalText("operations", "no_note_8cb0e796")}</p>
            {selectedLineage?.draftSource === "questionnaire" ? (
              <p className="c-va-version-foot" role="note">
                {editorText("versions.questionnaire_draft")}
              </p>
            ) : null}
            {baseWarning === null ? null : (
              <p className="c-va-alert c-va-alert-critical c-va-alert-flush" role="note">
                <span className="c-va-alert-icon">
                  <Icon glyph={GLYPH.warningCircle} size="lg" />
                </span>
                <span>{baseWarning}</span>
              </p>
            )}

            <p className="c-va-version-what">{portalText("operations", "what_changed_7e4b9199")}</p>
            {selected.change.length === 0 ? (
              <p className="c-note">{portalText("operations", "the_factory_recorded_no_change_list_for_this_version_5fd2e825")}</p>
            ) : (
              <ul className="c-va-change-list">
                {selected.change.map((change) => (
                  <li key={`${change.kind}:${change.subject}`}>
                    {change.detail === "" ? (
                      change.subject
                    ) : (
                      <>
                        <strong>{change.subject}</strong> — {change.detail}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {selectedFigures === undefined ? null : (
              <div className="c-va-version-figures">
                <span>
                  <span className="c-va-version-figure">
                    {selectedFigures.conversationCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))}
                  </span>
                  <span className="c-va-version-figure-label">{portalText("operations", "calls_served_f11287d5")}</span>
                </span>
                <span>
                  <span className="c-va-version-figure">
                    {selectedFigures.conversationCount === 0
                      ? "—"
                      : `${String(
                          Math.round(
                            (selectedFigures.outcomeReachedCount / selectedFigures.conversationCount) * 100,
                          ),
                        )}%`}
                  </span>
                  <span className="c-va-version-figure-label">
                    {analytics.data?.successMetricLabel ?? voiceAgent.data.successMetricLabel}
                  </span>
                  {delta === null ? null : (
                    <span className="c-va-version-delta" data-up={delta.up ? "true" : "false"}>
                      {delta.text}
                    </span>
                  )}
                </span>
              </div>
            )}

            <div className="c-va-version-actions">
              {selected.isLive ? (
                <Button
                  variant="secondary"
                  disabled={editRefusal !== null || editing !== null}
                  {...(editRefusal === null ? {} : { title: editRefusal })}
                  onClick={() => {
                    setEdited(null);
                    setEditing({ agentVersionId: null });
                  }}
                >
                  {portalText("operations", "edit_from_here_611edc59")}</Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={selected.state !== "ready" || publish.isPending}
                  onClick={() => {
                    publish.mutate({
                      agentVersionId: selected.agentVersionId,
                      label: selected.label,
                      everPublished: selected.publishedAtText !== null,
                      // The warning above says what this replaces; publishing with it on screen is the acknowledgement.
                      acknowledgeBaseChange: baseWarning !== null,
                    });
                  }}
                >
                  <Icon
                    glyph={selected.publishedAtText === null ? GLYPH.rocketLaunch : GLYPH.clockCounterClockwise}
                    size="md"
                  />
                  <span>
                    {publish.isPending
                      ? portalText("operations", "working_13b7bfca")
                      : selected.publishedAtText === null
                        ? portalText("operations", "publish_value_05e1503f", { v0: String(selected.label) })
                        : portalText("operations", "restore_value_45f5bb8e", { v0: String(selected.label) })}
                  </span>
                </Button>
              )}
              {baseWarning !== null && selectedLineage !== undefined ? (
                <RecreateButton
                  lineage={selectedLineage}
                  pending={recreate.isPending}
                  editRefusal={editRefusal}
                  onRecreate={(source) => {
                    recreate.mutate(source);
                  }}
                  onEditAgain={() => {
                    // plan-021 review L6: a pass-target draft's list is edited on Channels, from the published list.
                    if (selectedLineage.draftSource === "pass_targets") {
                      props.onOpenChannelBindingTab();
                      return;
                    }
                    // plan-030 WAPP-Q: a questionnaire draft's questions are edited on the Questions tab.
                    if (selectedLineage.draftSource === "questionnaire" && props.onOpenQuestions !== null) {
                      props.onOpenQuestions(null);
                      return;
                    }
                    setEdited(null);
                    setEditing({ agentVersionId: null });
                  }}
                />
              ) : null}
              {isDraft(selected) ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTestingVersionId(testingVersionId === selected.agentVersionId ? null : selected.agentVersionId);
                  }}
                >
                  <Icon glyph={GLYPH.chatCircleDots} size="md" />
                  <span>{testingVersionId === selected.agentVersionId ? portalText("operations", "close_the_test_chat_05084ad3") : portalText("operations", "test_value_in_a_chat_fa4f39c5", { v0: String(selected.label) })}</span>
                </Button>
              ) : null}
              {survey && props.onOpenQuestions !== null && (selected.state === "ready" || selected.isLive) ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    props.onOpenQuestions?.(selected.agentVersionId);
                  }}
                >
                  <Icon glyph={GLYPH.clipboardText} size="md" />
                  <span>{editorText("head.title")}</span>
                </Button>
              ) : null}
              {survey ? null : (
                <Button
                  variant="ghost"
                  disabled
                  title={portalText("operations", "downloading_a_version_s_value_arrives_soon_e0454fc6", { v0: String(portalText("operations", "menu_252a2566")) })}
                >
                  {portalText("operations", "download_menu_3db38866")}
                </Button>
              )}
            </div>
            {/* plan-030 WAPP-Q: a survey version's questions, as a file — every language's, one at a time. */}
            {survey && (selected.state === "ready" || selected.isLive) ? (
              <div className="c-va-version-actions">
                {(selected.languageCode.length === 0 ? [voiceAgent.data.languageCode[0] ?? ""] : selected.languageCode).map((languageCode) => (
                  <DownloadActions
                    key={languageCode}
                    voiceAgentId={props.voiceAgentId}
                    agentVersionId={selected.agentVersionId}
                    agentVersionLabel={selected.label}
                    languages={selected.languageCode}
                    language={languageCode}
                    displayName={voiceAgent.data.displayName}
                  />
                ))}
              </div>
            ) : null}
            {survey && isDraft(selected) && questionnaire.data !== undefined ? (
              <CampaignDiallingWarning campaign={questionnaire.data.campaignDialling} />
            ) : null}
            {testingVersionId === selected.agentVersionId && selected.state === "ready" ? (
              <div className="c-va-version-test">
                <p className="c-note">
                  {portalText("operations", "a_free_text_test_on_5b37cc25")}{selected.label}{portalText("operations", "before_it_is_published_it_answers_from_this_version_and_ca_654b4b39")}{liveVersion?.label ?? portalText("operations", "the_live_version_ca0fc912")}{portalText("operations", "nothing_it_says_it_will_do_is_carried_out_ccf61e59")}</p>
                <TestChatPanel
                  voiceAgentId={props.voiceAgentId}
                  voiceAgentName={`${voiceAgent.data.displayName} · ${selected.label}`}
                  languageCode={selected.languageCode}
                  agentVersionId={selected.agentVersionId}
                  onClose={() => {
                    setTestingVersionId(null);
                  }}
                  onOpenTestCall={null}
                />
              </div>
            ) : null}
            {selected.state === "ready" || selected.isLive ? null : selected.state === "building" ? (
              <p className="c-note" role="status">
                {selected.label} {portalText("operations", "is_building_it_takes_a_few_minutes_this_page_updates_by_it_154ecdaf")}{" "}
                {liveVersion === undefined ? "" : portalText("operations", "value_keeps_answering_calls_until_you_publish_value_a81a9bc1", { v0: String(liveVersion.label), v1: String(selected.label) })}
              </p>
            ) : (
              <p className="c-note">
                {portalText("operations", "only_a_version_whose_build_finished_and_whose_prompt_was_m_3090f4b1")}{selected.state}.
              </p>
            )}
            {!selected.isLive && selected.publishedAtText !== null ? (
              <p className="c-va-version-foot">
                {portalText("operations", "restoring_publishes_this_version_straight_away_calls_in_pr_a08ec503")}</p>
            ) : null}
            {isDraft(selected) ? (
              <p className="c-va-version-foot">
                {portalText("operations", "a_draft_not_answering_calls_until_you_publish_it_test_set_c8834049")}{" "}
                {draftTestRunText(testSuite.data, selected.label)}{portalText("operations", "you_decide_whether_to_publish_5e017135")}</p>
            ) : null}
          </div>
        )}
      </div>

      {/* plan-018 WAPP-B: what the selected version answers from, for a version with an information index only. */}
      {selected === undefined ? null : (
        <InformationPanel
          key={selected.agentVersionId}
          voiceAgentId={props.voiceAgentId}
          selected={selected}
          liveLabel={liveVersion?.label ?? null}
          onReread={(made) => {
            setReread(made);
            props.onSelect(made.agentVersionId);
          }}
        />
      )}

      <h2 className="c-va-improve-title">{portalText("operations", "improve_your_agent_baa5b6d2")}</h2>
      {/* Kept mounted while the Edit panel is open: "Review by hand" opens it, and the review must survive that. */}
      {liveVersion === undefined ? null : (
        <OptimisationReviewPanel
          voiceAgentId={props.voiceAgentId}
          liveVersionSeq={voiceAgent.data.liveVersionSeq}
          // plan-032 WAPP-OR: where an advice finding's "Change it yourself" opens (Channels; a survey's Questions).
          onOpenChannelBindingTab={props.onOpenChannelBindingTab}
          {...(props.onOpenQuestions === null ? {} : { onOpenQuestions: () => props.onOpenQuestions?.(null) })}
          {...(editRefusal === null && editing === null
            ? {
                onEditByHand: () => {
                  setEdited(null);
                  setEditing({ agentVersionId: null });
                },
              }
            : {})}
        />
      )}
      <TestSuiteCard suite={testSuite.data} hasDraft={all.some(isDraft)} />
    </>
  );
}

/** A built version nobody has published yet: the design's "Draft". */
function isDraft(one: AgentVersionSummaryView): boolean {
  return !one.isLive && one.state === "ready" && one.publishedAtText === null;
}

/** The draft with the highest ordinal — the one a save just made. */
function newestDraftOf(all: readonly AgentVersionSummaryView[]): AgentVersionSummaryView | undefined {
  return all.filter(isDraft).sort((left, right) => right.ordinal - left.ordinal)[0];
}

/**
 * Beside a draft whose base is no longer live (plan-015 DRF): an answers or review draft is made again on the
 * live version ("Recreate on the current version"); any other draft — an edit's, or a pass-target draft's
 * (plan-021, `pass_targets`) — is edited again from the live version ("Edit again"), because its answers are a whole
 * document rather than a change to re-apply. A pass-target draft's "Edit again" opens the Channels tab, where the list
 * is edited (plan-021 review L6).
 */
function RecreateButton({
  lineage,
  pending,
  editRefusal,
  onRecreate,
  onEditAgain,
}: {
  readonly lineage: AgentVersionLineageView;
  readonly pending: boolean;
  readonly editRefusal: string | null;
  readonly onRecreate: (
    source: { readonly kind: "answers" } | { readonly kind: "optimisation_review"; readonly optimisationReviewId: string },
  ) => void;
  readonly onEditAgain: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const optimisationReviewId = lineage.draftOptimisationReviewId;
  if (lineage.draftSource === "answers" || (lineage.draftSource === "optimisation_review" && optimisationReviewId !== null)) {
    return (
      <Button
        variant="secondary"
        disabled={pending}
        onClick={() => {
          onRecreate(optimisationReviewId === null || lineage.draftSource === "answers" ? { kind: "answers" } : { kind: "optimisation_review", optimisationReviewId: optimisationReviewId });
        }}
      >
        <Icon glyph={GLYPH.arrowCounterClockwise} size="md" />
        <span>{pending ? portalText("operations", "working_13b7bfca") : portalText("operations", "recreate_on_the_current_version_3143b07d")}</span>
      </Button>
    );
  }
  return (
    <Button
      variant="secondary"
      disabled={editRefusal !== null}
      {...(editRefusal === null ? {} : { title: editRefusal })}
      onClick={() => {
        onEditAgain();
      }}
    >
      {portalText("operations", "edit_again_01bf9ea7")}</Button>
  );
}

/**
 * "+3 pts vs v7": the selected version's success share against the published version before it, or
 * `null` when either has no conversations to measure (design sync 2026-10-09).
 */
export function successDeltaOf(
  selected: AgentVersionSummaryView,
  all: readonly AgentVersionSummaryView[],
  figuresOf: (agentVersionId: string) => AgentVersionComparisonView | undefined,
): { readonly text: string; readonly up: boolean } | null {
  const previous = all
    .filter((one) => one.ordinal < selected.ordinal && one.publishedAtText !== null)
    .sort((left, right) => right.ordinal - left.ordinal)[0];
  if (previous === undefined) return null;
  const share = (agentVersionId: string): number | null => {
    const figures = figuresOf(agentVersionId);
    if (figures === undefined || figures.conversationCount === 0) return null;
    return (figures.outcomeReachedCount / figures.conversationCount) * 100;
  };
  const mine = share(selected.agentVersionId);
  const theirs = share(previous.agentVersionId);
  if (mine === null || theirs === null) return null;
  const delta = Math.round(mine - theirs);
  return { get text() { return portalText("operations", "valuevalue_pts_vs_value_f4694b52", { v0: String(delta >= 0 ? "+" : "−"), v1: String(String(Math.abs(delta))), v2: String(previous.label) }); }, up: delta >= 0 };
}

/** The design's version tags: Live, Previous, Rolled back, Archived — and the build states. */
function AgentVersionStateTag({
  one,
  liveAgentVersion,
}: {
  readonly one: AgentVersionSummaryView;
  readonly liveAgentVersion: AgentVersionSummaryView | undefined;
}): ReactNode {
  usePortalTranslation("operations");
  if (one.isLive) return <Tag status="positive">{portalText("operations", "live_65c821a5")}</Tag>;
  if (one.state === "archived") return <Tag tone="neutral">{portalText("operations", "archived_eddc813f")}</Tag>;
  if (one.state === "failed") return <Tag status="critical">{portalText("operations", "failed_09fef5d8")}</Tag>;
  if (one.state === "building") return <Tag status="caution">{portalText("operations", "building_9d914bfc")}</Tag>;
  if (one.publishedAtText === null) return <Tag tone="neutral">{portalText("operations", "draft_23d33e22")}</Tag>;
  if (liveAgentVersion !== undefined && one.ordinal > liveAgentVersion.ordinal) return <Tag tone="neutral">{portalText("operations", "rolled_back_71df6d38")}</Tag>;
  return <Tag tone="neutral">{portalText("operations", "previous_50f94286")}</Tag>;
}

/**
 * Who made it, by name (customer-portal review #95). A member is named from `authored_by_name`;
 * WeTalk acting for the account says so; a version nobody authored came from an automatic process.
 */
export function authorOf(one: AgentVersionSummaryView): string {
  if (one.authoredBy === "operator") return portalText("operations", "wetalk_on_your_behalf_f05a4566");
  if (one.authorName !== null) return one.authorName;
  return one.authoredBy === "member" ? portalText("operations", "a_former_member_of_your_team_2ded27cb") : portalText("operations", "an_automatic_process_489ac10e");
}

/**
 * "Published … by …" (review #95). The first build publishes itself (decision D14 (a)), so that is
 * said as what happened — published automatically when the author's build finished — never "someone".
 */
export function publisherOf(one: AgentVersionSummaryView): string {
  const publisher = one.publishedBy;
  if (publisher === null) return portalText("operations", "by_value_22095413", { v0: String(authorOf(one)) });
  if (publisher.kind === "person") return portalText("operations", "by_value_22095413", { v0: String(publisher.name ?? portalText("operations", "a_former_member_of_your_team_2ded27cb")) });
  if (publisher.kind === "operator") return publisher.name === null ? portalText("operations", "by_wetalk_on_your_behalf_59f56209") : portalText("operations", "by_value_wetalk_42185026", { v0: String(publisher.name) });
  return one.authorName === null ? portalText("operations", "automatically_when_its_build_finished_7f2ad5d1") : portalText("operations", "automatically_when_value_s_build_finished_96c8b75a", { v0: String(one.authorName) });
}

/** The list's by-line: who, then how many conversations it has served. */
function authorLine(one: AgentVersionSummaryView): string {
  const who = authorOf(one);
  return who.charAt(0).toUpperCase() + who.slice(1);
}

/** What the last Edit did, in the words the owner needs next (review #75). */
function EditedNotice({ outcome, liveLabel }: { readonly outcome: EditOutcome; readonly liveLabel: string | null }): ReactNode {
  usePortalTranslation("operations");
  return (
    <p className="c-va-alert" role="status">
      <span className="c-va-alert-icon">
        <Icon glyph={GLYPH.checkCircle} size="lg" />
      </span>
      {outcome.kind === "recording" ? (
        <span>
          {portalText("operations", "recording_is_now_91cec19b")}{outcome.recordingEnabled ? portalText("operations", "on_db3d405b") : portalText("operations", "off_da7a6873")} {portalText("operations", "for_new_calls_no_build_was_needed_calls_in_progress_keep_t_b750c728")}</span>
      ) : (
        <span>
          {outcome.label} {portalText("operations", "is_building_from_your_changes_38d8abc1")}{liveLabel === null ? "" : portalText("operations", "value_keeps_answering_calls_43a3b8ea", { v0: String(liveLabel) })}
          {portalText("operations", "when_197c54b6")}{outcome.label} {portalText("operations", "is_built_try_it_here_in_a_chat_then_publish_it_9dc0d678")}</span>
      )}
    </p>
  );
}

/** "4 of 5 passed" when the last run was against this draft, else "not run yet". */
function draftTestRunText(suite: readonly TestSuiteView[] | undefined, label: string): string {
  const run = suite?.find((one) => one.lastRunAgainstLabel === label && one.lastRunAtText !== null);
  if (run === undefined) return portalText("operations", "not_run_yet_aa5d468d");
  const passed = run.testCase.filter((testCase) => testCase.passed === true).length;
  return portalText("operations", "value_of_value_passed_dd614eda", { v0: String(String(passed)), v1: String(String(run.testCase.length)) });
}

/**
 * The test-set card.
 *
 * `undefined` is "still loading", an empty array is "no suite exists", and a suite with no
 * run is "never run" — three different sentences, because they are three different facts and
 * one of them ("no suite") is something the customer can fix.
 */
function TestSuiteCard({
  suite,
  hasDraft,
}: {
  readonly suite: readonly TestSuiteView[] | undefined;
  /** A draft exists to run against (the design draws "Run against the draft" only then). */
  readonly hasDraft: boolean;
}): ReactNode {
  usePortalTranslation("operations");
  if (suite === undefined) return <p className="c-note">{portalText("operations", "reading_the_test_set_8718af61")}</p>;
  if (suite.length === 0) {
    return (
      <div className="c-va-card c-va-test-set">
        <p className="c-va-card-title">{portalText("operations", "no_test_set_yet_a173d8e5")}</p>
        <p className="c-va-card-dek">
          {portalText("operations", "a_test_set_is_a_handful_of_things_this_agent_must_get_righ_eb563eb2")}</p>
      </div>
    );
  }

  return (
    <>
      {suite.map((one) => {
        const total = one.testCase.length;
        const passed = one.testCase.filter((testCase) => testCase.passed === true).length;
        return (
          <div key={one.testSuiteId} className="c-va-card c-va-test-set">
            <div className="c-va-test-set-head">
              <div className="c-va-test-set-copy">
                <p className="c-va-card-title">
                  {portalText("operations", "test_set_49feded2")}{" "}
                  {one.lastRunAtText === null
                    ? portalText("operations", "never_run_43a2d42a")
                    : portalText("operations", "last_run_value_value_of_value_passed_on_value_01929aa0", { v0: String(one.lastRunAtText), v1: String(String(passed)), v2: String(String(total)), v3: String(one.lastRunAgainstLabel ?? portalText("operations", "an_unnamed_version_67f7687b")) })}
                </p>
                <p className="c-va-card-dek">
                  {one.description ?? portalText("operations", "value_checked_against_the_draft_before_you_publish_28e21eee", { v0: String(one.name) })}
                </p>
              </div>
              <div className="c-va-actions">
                <Button variant="secondary" disabled>
                  {portalText("operations", "add_a_case_soon_7acfc870")}</Button>
                {hasDraft ? (
                  <Button variant="primary" disabled>
                    <Icon glyph={GLYPH.play} size="md" />
                    <span>{portalText("operations", "run_against_the_draft_soon_b71ca6b4")}</span>
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="c-va-test-cases">
              {one.testCase.map((testCase) => (
                <TestCaseRow
                  key={testCase.testCaseId}
                  testCase={testCase}
                  againstLabel={one.lastRunAgainstLabel}
                />
              ))}
            </div>

            {one.failedCount === 0 ? null : (
              <p className="c-va-alert c-va-alert-critical c-va-alert-flush" role="status">
                <span className="c-va-alert-icon">
                  <Icon glyph={GLYPH.warning} size="lg" />
                </span>
                <span>
                  {one.failedCount === 1
                    ? portalText("operations", "one_case_failed_on_the_draft_4d459660")
                    : portalText("operations", "value_cases_failed_on_the_draft_cba4a0b3", { v0: String(String(one.failedCount)) })}{" "}
                  {portalText("operations", "you_can_still_publish_the_result_is_kept_with_the_version_804dd901")}</span>
              </p>
            )}
          </div>
        );
      })}
    </>
  );
}

/**
 * One case.
 *
 * Three states, three renderings. `passed: null` is "not in the last run" and is NOT drawn as
 * a pass: a case the run skipped is a case nobody has evidence about.
 */
function TestCaseRow({
  testCase,
  againstLabel,
}: {
  readonly testCase: TestCaseResultView;
  /** The version the last run was against — the design's "Passed on v7". */
  readonly againstLabel: string | null;
}): ReactNode {
  usePortalTranslation("operations");
  const against = againstLabel === null ? "" : portalText("operations", "on_value_7205b05e", { v0: String(againstLabel) });
  const glyph =
    testCase.passed === null
      ? GLYPH.circleDashed
      : testCase.passed
        ? GLYPH.checkCircle
        : GLYPH.xCircle;
  const state = testCase.passed === null ? "none" : testCase.passed ? "passed" : "failed";

  return (
    <div className="c-va-test-case" data-state={state}>
      <span className="c-va-test-case-mark">
        <Icon glyph={glyph} size="xl" />
      </span>
      <span className="c-va-test-case-body">
        <span className="c-va-test-case-name">{testCase.utterance}</span>
        <span className="c-va-test-case-expect">{testCase.expectation}</span>
        {testCase.passed === false && testCase.resultText !== null ? (
          <span className="c-va-test-case-failure">{testCase.resultText}</span>
        ) : null}
        {testCase.passed === false && testCase.transcriptExcerpt !== null ? (
          <span className="c-va-test-case-quote">{testCase.transcriptExcerpt}</span>
        ) : null}
      </span>
      <span className="c-va-test-case-result">
        {testCase.passed === null
          ? portalText("operations", "not_run_9e019cd5")
          : testCase.passed
            ? portalText("operations", "passedvalue_65caca17", { v0: String(against) })
            : portalText("operations", "failedvalue_d5745d9d", { v0: String(against) })}
      </span>
    </div>
  );
}
